import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, DollarSign,
  BarChart3, ChevronDown, ChevronUp, Trophy, AlertTriangle,
  CheckCircle2, Target, CreditCard, FileText, Zap, Minus,
  Activity, Clock, Lightbulb
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart, Legend
} from 'recharts';
import api from '../../utils/api';


// ── KPI Card ─────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <Card className="border border-slate-200" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-[#122033] truncate">{value}</p>
        <p className="text-[10px] text-slate-400">{label}</p>
        {sub && <p className="text-[10px] text-slate-300">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

// ── Insight Card ─────────────────────────────────────────────
const InsightCard = ({ insight }) => {
  const isWarning = insight.type === 'warning';
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${isWarning ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}
      data-testid={`insight-${insight.campaign_id}`}>
      {isWarning ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{insight.title}</p>
        <p className={`text-[10px] ${isWarning ? 'text-amber-600' : 'text-emerald-600'}`}>{insight.message}</p>
      </div>
    </div>
  );
};

// ── Campaign Table Row ───────────────────────────────────────
const CampaignRow = ({ campaign, rank }) => {
  const convColor = campaign.conversion_rate >= 20 ? 'text-emerald-600' : campaign.conversion_rate >= 5 ? 'text-amber-600' : 'text-slate-400';
  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    scheduled: 'bg-blue-100 text-blue-700',
    draft: 'bg-slate-100 text-slate-500',
    ended: 'bg-red-50 text-red-500',
    paused: 'bg-amber-100 text-amber-700',
  };
  const statusCls = statusColors[campaign.status] || statusColors.draft;

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`campaign-row-${campaign.campaign_id}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] text-slate-400 font-medium">#{rank}</span>
          <span className="text-xs font-medium text-slate-800 truncate max-w-[180px]" data-testid={`campaign-title-${campaign.campaign_id}`}>{campaign.title}</span>
          <Badge className={`text-[8px] ${statusCls} shrink-0`}>{campaign.status}</Badge>
        </div>
      </td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`campaign-quotes-${campaign.campaign_id}`}>{campaign.quotes_count}</td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`campaign-accepted-${campaign.campaign_id}`}>{campaign.offers_accepted}</td>
      <td className="px-3 py-2.5 text-center text-xs text-slate-600" data-testid={`campaign-payments-${campaign.campaign_id}`}>{campaign.payments_completed}</td>
      <td className="px-3 py-2.5 text-center">
        <span className={`text-xs font-medium ${convColor}`} data-testid={`campaign-conv-${campaign.campaign_id}`}>
          {campaign.conversion_rate}%
        </span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className="text-xs font-medium text-slate-700" data-testid={`campaign-revenue-${campaign.campaign_id}`}>
          {campaign.revenue_generated > 0 ? `€${campaign.revenue_generated.toLocaleString()}` : '-'}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center">
        <Badge className={`text-[8px] ${
          campaign.health_badge === 'Strong' ? 'bg-emerald-100 text-emerald-700' :
          campaign.health_badge === 'Moderate' ? 'bg-amber-100 text-amber-700' :
          'bg-slate-100 text-slate-400'
        }`}>{campaign.health_badge}</Badge>
      </td>
    </tr>
  );
};

// ── Momentum Badge ───────────────────────────────────────────
const MomentumBadge = ({ momentum, testId }) => {
  if (!momentum) return null;
  const config = {
    growing: { icon: TrendingUp, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', arrow: '+' },
    declining: { icon: TrendingDown, cls: 'bg-red-100 text-red-600 border-red-200', arrow: '' },
    stable: { icon: Minus, cls: 'bg-slate-100 text-slate-500 border-slate-200', arrow: '' },
  };
  const c = config[momentum.badge] || config.stable;
  const Icon = c.icon;
  return (
    <Badge className={`text-[10px] gap-1 border ${c.cls}`} data-testid={testId}>
      <Icon className="h-3 w-3" />
      {momentum.label} {momentum.change_pct !== 0 && `(${c.arrow}${momentum.change_pct}%)`}
    </Badge>
  );
};

// ── Custom Chart Tooltip ─────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-[10px] font-medium text-slate-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-[10px]" style={{ color: p.color }}>
          {p.name}: <span className="font-medium">{p.name === 'Revenue' ? `€${p.value.toLocaleString()}` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ── Trend Insight Card ───────────────────────────────────────
const TrendInsightCard = ({ insight }) => {
  const typeConfig = {
    success: { cls: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2, iconCls: 'text-emerald-500', textCls: 'text-emerald-700' },
    warning: { cls: 'bg-amber-50 border-amber-200', icon: AlertTriangle, iconCls: 'text-amber-500', textCls: 'text-amber-700' },
    info: { cls: 'bg-blue-50 border-blue-200', icon: Activity, iconCls: 'text-blue-500', textCls: 'text-blue-700' },
  };
  const c = typeConfig[insight.type] || typeConfig.info;
  const Icon = c.icon;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${c.cls}`} data-testid={`trend-insight-${insight.type}`}>
      <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${c.iconCls}`} />
      <p className={`text-xs ${c.textCls}`}>{insight.message}</p>
    </div>
  );
};

// ── Advisor Recommendation Card ──────────────────────────────
const AdvisorCard = ({ advisory }) => {
  const levelConfig = {
    critical: { cls: 'border-red-200 bg-red-50', headerCls: 'text-red-700', badgeCls: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
    warning: { cls: 'border-amber-200 bg-amber-50', headerCls: 'text-amber-700', badgeCls: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
    tip: { cls: 'border-emerald-200 bg-emerald-50', headerCls: 'text-emerald-700', badgeCls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Lightbulb },
    none: { cls: 'border-slate-200 bg-slate-50', headerCls: 'text-slate-500', badgeCls: 'bg-slate-100 text-slate-500', icon: Minus },
  };
  const lc = levelConfig[advisory.recommendation_level] || levelConfig.none;
  const LevelIcon = lc.icon;

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${lc.cls}`} data-testid={`advisor-card-${advisory.campaign_id}`}>
      <div className="flex items-center gap-2">
        <LevelIcon className={`h-3.5 w-3.5 shrink-0 ${lc.headerCls}`} />
        <span className="text-xs font-medium text-slate-800 truncate flex-1">{advisory.campaign_title}</span>
        <Badge className={`text-[8px] border ${lc.badgeCls}`}>{advisory.recommendation_level}</Badge>
      </div>
      {advisory.advisor_messages.map((msg, i) => (
        <div key={i} className="pl-5">
          <p className="text-xs text-slate-700">{msg.message}</p>
          {msg.detail && <p className="text-[10px] text-slate-400 mt-0.5">{msg.detail}</p>}
        </div>
      ))}
    </div>
  );
};

// ── Main Dashboard ───────────────────────────────────────────
export default function ClinicCampaignPerformance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [advisorLoading, setAdvisorLoading] = useState(true);
  const [sortField, setSortField] = useState('revenue_generated');
  const [sortDir, setSortDir] = useState('desc');
  const [trendRange, setTrendRange] = useState(12);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const token = localStorage.getItem('clinic_admin_token') || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clinic/campaigns/performance`, {
      });
      const d = res.data;
      if (d.success) setData(d);
    } catch {}
    setLoading(false);
  }, [token]);

  const fetchTrends = useCallback(async () => {
    setTrendsLoading(true);
    try {
      const url = selectedCampaignId
        ? `/clinic/campaigns/performance/trends?weeks=${trendRange}&campaign_id=${selectedCampaignId}`
        : `/clinic/campaigns/performance/trends?weeks=${trendRange}`;
      const res = await fetch(url, {
      });
      const d = res.data;
      if (d.success) setTrends(d);
    } catch {}
    setTrendsLoading(false);
  }, [token, trendRange, selectedCampaignId]);

  const fetchAdvisor = useCallback(async () => {
    setAdvisorLoading(true);
    try {
      const res = await api.get(`/clinic/campaigns/advisor`, {
      });
      const d = res.data;
      if (d.success) setAdvisor(d);
    } catch {}
    setAdvisorLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); fetchAdvisor(); }, [fetchData, fetchAdvisor]);
  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sorted = data ? [...(data.campaigns || [])].sort((a, b) => {
    const av = a[sortField] ?? -1;
    const bv = b[sortField] ?? -1;
    return sortDir === 'desc' ? bv - av : av - bv;
  }) : [];

  const SortIcon = ({ field }) => {
  const { t } = useTranslation();
    if (sortField !== field) return <ChevronDown className="h-3 w-3 text-slate-300" />;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3 text-emerald-600" /> : <ChevronUp className="h-3 w-3 text-emerald-600" />;
  };

  const summary = data?.summary || {};
  const leaderboard = data?.leaderboard || [];
  const insights = data?.insights || [];
  const uniqueInsights = insights.reduce((acc, ins) => {
    if (!acc.find(i => i.campaign_id === ins.campaign_id && i.type === ins.type)) acc.push(ins);
    return acc;
  }, []).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="clinic-campaign-performance">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/clinic/campaigns')} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-[#122033] font-[Manrope]" data-testid="page-title">{t('provider.campaigns.performance.title')}</h1>
            <p className="text-[10px] text-slate-400">{t('provider.campaigns.performance.subtitle')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <BarChart3 className="h-3 w-3" />}
            <span className="ml-1">{t('provider.campaigns.performance.refresh')}</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : (
          <>
            {/* Section 1: KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-section">
              <KpiCard icon={FileText} label={t('provider.campaigns.metrics.campaignQuotes')} value={summary.total_quotes || 0} color="bg-blue-500" testId="kpi-quotes" />
              <KpiCard icon={CreditCard} label={t('provider.campaigns.metrics.paymentsCompleted')} value={summary.total_payments || 0} color="bg-emerald-500" testId="kpi-payments" />
              <KpiCard icon={Target} label={t('provider.campaigns.metrics.conversionRate')} value={`${summary.avg_conversion_rate || 0}%`} color="bg-amber-500" testId="kpi-conversion" />
              <KpiCard icon={DollarSign} label={t('provider.campaigns.metrics.revenueGenerated')} value={`€${(summary.total_revenue || 0).toLocaleString()}`} color="bg-violet-500" testId="kpi-revenue" />
            </div>

            {/* Trends Section */}
            <Card className="border border-slate-200" data-testid="trends-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                      <Activity className="h-4 w-4 text-blue-500" /> Performance Trends
                    </CardTitle>
                    {trends?.momentum && <MomentumBadge momentum={trends.momentum} testId="global-momentum" />}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[4, 12, 26].map(w => (
                      <Button
                        key={w}
                        size="sm"
                        variant={trendRange === w ? 'default' : 'outline'}
                        className={`text-[10px] h-6 px-2 ${trendRange === w ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                        onClick={() => setTrendRange(w)}
                        data-testid={`range-${w}w`}
                      >
                        {w === 4 ? '4 Weeks' : w === 12 ? '12 Weeks' : '6 Months'}
                      </Button>
                    ))}
                  </div>
                </div>
                {/* Campaign filter */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Button
                    size="sm" variant={!selectedCampaignId ? 'default' : 'outline'}
                    className={`text-[10px] h-6 px-2 ${!selectedCampaignId ? 'bg-slate-700' : ''}`}
                    onClick={() => setSelectedCampaignId(null)}
                    data-testid="filter-all"
                  >{t('provider.campaigns.performance.allCampaigns')}</Button>
                  {Object.values(trends?.per_campaign || {}).slice(0, 5).map(c => (
                    <Button
                      key={c.campaign_id} size="sm"
                      variant={selectedCampaignId === c.campaign_id ? 'default' : 'outline'}
                      className={`text-[10px] h-6 px-2 truncate max-w-[140px] ${selectedCampaignId === c.campaign_id ? 'bg-slate-700' : ''}`}
                      onClick={() => setSelectedCampaignId(c.campaign_id)}
                      data-testid={`filter-camp-${c.campaign_id}`}
                    >{c.title}</Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {trendsLoading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>
                ) : (
                  <div className="h-64" data-testid="trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trends?.series || []} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                        <defs>
                          <linearGradient id="quotesGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="acceptsGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 10 }} />
                        <Area type="monotone" dataKey="quotes" name="Quotes" stroke="#3b82f6" fill="url(#quotesGrad)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="accepts" name="Accepted" stroke="#10b981" fill="url(#acceptsGrad)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#8b5cf6" fill="url(#revenueGrad)" strokeWidth={2} dot={false} yAxisId="right" hide />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Momentum + Timeline + Insights row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Campaign Momentum Indicators */}
              <Card className="border border-slate-200" data-testid="momentum-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" /> Campaign Momentum
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.values(trends?.per_campaign || {}).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">{t('provider.campaigns.performance.noTrend')}</p>
                  ) : Object.values(trends?.per_campaign || {}).slice(0, 6).map((c) => (
                    <div key={c.campaign_id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100" data-testid={`momentum-${c.campaign_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{c.title}</p>
                      </div>
                      <MomentumBadge momentum={c.momentum} testId={`momentum-badge-${c.campaign_id}`} />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Performance Timeline */}
              <Card className="border border-slate-200" data-testid="timeline-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" /> Weekly Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 max-h-52 overflow-y-auto">
                  {(trends?.series || []).filter(s => s.quotes > 0).reverse().map((s, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50" data-testid={`timeline-week-${s.week}`}>
                      <span className="text-[10px] text-slate-400 w-14 shrink-0">{s.label}</span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${Math.min(s.quotes * 8, 100)}%` }} />
                      </div>
                      <span className="text-[10px] font-medium text-slate-600 w-16 text-right">{s.quotes} quotes</span>
                    </div>
                  ))}
                  {!(trends?.series || []).some(s => s.quotes > 0) && (
                    <p className="text-xs text-slate-400 text-center py-4">{t('provider.campaigns.performance.noActivity')}</p>
                  )}
                </CardContent>
              </Card>

              {/* Auto Trend Insights */}
              <Card className="border border-slate-200" data-testid="trend-insights-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Zap className="h-4 w-4 text-violet-500" /> Trend Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(trends?.insights || []).length === 0 && (data?.insights || []).length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No insights yet — check back as data accumulates</p>
                  ) : (
                    <>
                      {(trends?.insights || []).map((ins, i) => <TrendInsightCard key={`t-${i}`} insight={ins} />)}
                      {(data?.insights || []).filter(ins => !(trends?.insights || []).some(t => t.message === ins.message)).slice(0, 3).map((ins, i) => <InsightCard key={`d-${i}`} insight={ins} />)}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
              <Card className="border border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white" data-testid="leaderboard-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-500" /> Top 5 Campaigns
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {leaderboard.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">{t('provider.campaigns.performance.noData')}</p>
                  ) : leaderboard.map((c, i) => (
                    <div key={c.campaign_id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-100" data-testid={`leader-${i}`}>
                      <span className={`text-sm font-bold ${i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : 'text-amber-700'}`}>#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 truncate">{c.title}</p>
                        <p className="text-[10px] text-slate-400">
                          {c.quotes_count} quotes, {c.offers_accepted} accepted
                        </p>
                      </div>
                      <span className="text-xs font-bold text-emerald-600">
                        {c.revenue_generated > 0 ? `€${c.revenue_generated.toLocaleString()}` : '-'}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

            {/* Campaign Advisor Section */}
            <Card className="border-2 border-violet-200 bg-gradient-to-br from-violet-50/30 to-white" data-testid="advisor-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-violet-500" /> Campaign Advisor
                    {advisor?.summary && (
                      <div className="flex gap-1 ml-1">
                        {advisor.summary.critical_count > 0 && <Badge className="text-[8px] bg-red-100 text-red-700 border border-red-200">{advisor.summary.critical_count} critical</Badge>}
                        {advisor.summary.warning_count > 0 && <Badge className="text-[8px] bg-amber-100 text-amber-700 border border-amber-200">{advisor.summary.warning_count} warnings</Badge>}
                        {advisor.summary.tip_count > 0 && <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border border-emerald-200">{advisor.summary.tip_count} tips</Badge>}
                      </div>
                    )}
                  </CardTitle>
                  <p className="text-[10px] text-slate-400">
                    {advisor?.summary?.campaigns_with_advice || 0} of {advisor?.summary?.campaigns_analyzed || 0} campaigns have recommendations
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {advisorLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-violet-400" /></div>
                ) : (advisor?.advisories || []).length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">All campaigns are performing well — no recommendations needed.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1" data-testid="advisor-list">
                    {(advisor?.advisories || []).slice(0, 8).map(a => <AdvisorCard key={a.campaign_id} advisory={a} />)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard + Performance Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Leaderboard */}
              <Card className="lg:col-span-2 border border-slate-200" data-testid="insights-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                    <Zap className="h-4 w-4 text-violet-500" /> Performance Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {uniqueInsights.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">{t('provider.campaigns.performance.noInsights')}</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="insights-list">
                      {uniqueInsights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Section 2: Campaign Table */}
            <Card className="border border-slate-200" data-testid="campaign-table-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#122033] flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-slate-400" />{t('provider.campaigns.performance.allCampaigns')}<Badge className="text-[9px] bg-slate-100 text-slate-500">{sorted.length} campaigns</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left" data-testid="campaign-table">
                    <thead className="bg-slate-50 border-y border-slate-100">
                      <tr>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400">{t('provider.campaigns.performance.columns.campaign')}</th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('quotes_count')} data-testid="sort-quotes">
                          <span className="inline-flex items-center gap-0.5">{t('provider.campaigns.performance.columns.quotes')}<SortIcon field="quotes_count" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('offers_accepted')} data-testid="sort-accepted">
                          <span className="inline-flex items-center gap-0.5">{t('provider.campaigns.performance.columns.accepted')}<SortIcon field="offers_accepted" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('payments_completed')} data-testid="sort-payments">
                          <span className="inline-flex items-center gap-0.5">{t('provider.campaigns.performance.columns.payments')}<SortIcon field="payments_completed" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center cursor-pointer" onClick={() => toggleSort('conversion_rate')} data-testid="sort-conversion">
                          <span className="inline-flex items-center gap-0.5">{t('provider.campaigns.performance.columns.conversion')}<SortIcon field="conversion_rate" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-right cursor-pointer" onClick={() => toggleSort('revenue_generated')} data-testid="sort-revenue">
                          <span className="inline-flex items-center gap-0.5">{t('provider.campaigns.performance.columns.revenue')}<SortIcon field="revenue_generated" /></span>
                        </th>
                        <th className="px-3 py-2 text-[10px] font-medium text-slate-400 text-center">{t('provider.campaigns.performance.columns.health')}</th>
                      </tr>
                    </thead>
                    <tbody data-testid="campaign-rows">
                      {sorted.map((c, i) => <CampaignRow key={c.campaign_id} campaign={c} rank={i + 1} />)}
                      {!sorted.length && (
                        <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">{t('provider.campaigns.performance.empty')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
